import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import { withSnackbar } from 'notistack';

import { setPageHeaderTextActionCreator, setTableStateActionCreator } from 'plugins/store/reducers/common.reducer';
import { setAlertActionCreator } from 'plugins/store/reducers/common.reducer';
import { invite } from 'services/user.service';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import Button from 'components/CustomButtons/Button';
import InviteUserForm from 'components/InviteUserForm/InviteUserForm';
import PeopleOutlineIcon from '@material-ui/icons/PeopleOutline';
import SnackMessage from 'components/Snackbar/SnackMessage';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';

import ReactTable from 'components/ReactTable/ReactTable';
import { can } from 'utils/permissions/permissionsChecker';
import permissionsMap from 'utils/permissions/permissionsMap';
import { withStyles } from '@material-ui/core/styles';
import { searchUsers, deleteUserById } from 'services/user.service';
import DeleteIcon from "@material-ui/icons/Delete";
import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import styles from 'assets/jss/material-dashboard-pro-react/views/userTableStyle';
import Tooltip from '@material-ui/core/Tooltip';

class Users extends Component {
	viewName = 'Users';

	columns = [
		{
			Header: 'Full name',
			accessor: 'fullName',
		},
		{
			Header: 'Email',
			accessor: 'email',
		},
		{
			Header: 'Actions',
			accessor: 'actions',
			className: 'flag-column-right',
			disableFilters: true,
			disableSortBy: true,
			Cell: ({ row }) => {
				if (this.props.userId === row?.original?._id) {
					return null;
				}
				return (
					<div className={this.props.classes.actionsWrapper}>
						<Tooltip
							arrow
							title={"Remove"} >
							<DeleteIcon
								className={this.props.classes.actionIcon}
								onClick={() => this.onDeleteClick(row?.original?._id)}
							/>
						</Tooltip>
					</div>)
			},
		},
	];

	state = {
		data: [],
		totalRecordsCount: 0,
		pageCount: 0,
		loading: true,
	};

	componentDidMount() {
		this.props.setPageHeaderText(this.viewName);
	}

	componentWillUnmount() {
		this.props.setPageHeaderText('');
	}

	onSendHandler = async ({ role, email }) => {
		const { enqueueSnackbar, setAlert, } = this.props;
		try {
			await invite({ role, email });
			enqueueSnackbar('Link has been sent successfully', {
				content: (key, message) => <SnackMessage id={key} message={message} color='success' />,
			});
			setAlert(null);
		} catch (e) {
			enqueueSnackbar(e.resolvedErrorMessage, {
				content: (key, message) => <SnackMessage id={key} message={message} color='danger' />,
			});
		}
	};

	handleInvite = () => {
		const { setAlert, } = this.props;

		setAlert({
			type: 'withForm',
			title: 'Invite New User',
			confirmBtnText: 'Send',
			children: (
				<InviteUserForm />
			),
			onConfirm: async (data) => {
				this.onSendHandler(data);
			},
			onCancel: () => {
				setAlert(null);
			},
		});
	};

	fetchData = async ({
		filterQuery,
		pageIndex,
		pageSize,
		sortBy,
	}) => {
		this.setState({ loading: true });
		const result = await searchUsers({
			filterQuery,
			pageIndex,
			pageSize,
			sortBy,
		});

		this.setState({
			data: result.data,
			totalRecordsCount: result.totalCount,
			pageCount: Math.ceil(result.totalCount / pageSize),
			loading: false,
		});
		this.props.setRowsPerPage({
			table: 'users',
			rowsPerPage: pageSize,
		});
		this.props.setTableState({
			table: 'users',
			state: {
				searchQuery: filterQuery,
				sortBy: sortBy,
				page: pageIndex,
			},
		});
	};

	onDeleteClick = (userId) => {
		const {
			enqueueSnackbar,
			setAlert,
			rowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultPageIndex,
		} = this.props;

		const deleteNotifications = {
			type: 'warning',
			title: 'Do you want to delete this User?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteUserById(userId);
					this.fetchData({
						filterQuery: defaultSearchQuery,
						pageIndex: defaultPageIndex,
						pageSize: rowsPerPage,
						sortBy: defaultSortBy,
					});

					enqueueSnackbar(
						'User successfully removed',
						{
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="success"
								/>
							),
						}
					);
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => (
							<SnackMessage
								id={key}
								message={message}
								color="danger"
							/>
						),
					});
				}
			},
			onCancel: () => {
				setAlert(null);
			},
		};
		setAlert(deleteNotifications);
	};

	render() {
		const {
			classes,
			rowsPerPage,
			defaultSearchQuery,
			defaultSortBy,
			defaultPageIndex,
		} = this.props;

		return (
			<GridContainer>
				<GridItem xs={12} sm={12} md={12}>
					<Card>
						<CardHeader color="primary" icon>
							<CardIcon color="primary">
								<PeopleOutlineIcon />
							</CardIcon>
						</CardHeader>
						<CardBody>
							<GridContainer>
								<GridItem xs={12} sm={12} md={12}>
									<ReactTable
										isPageRootComponent
										clickableRows={false}
										rowsWithSelection={false}
										isAllowDelete={can(permissionsMap.adminPanel.items.userManagement.delete)}
										columns={this.columns}
										data={this.state.data}
										fetchData={this.fetchData}
										rowsPerPage={rowsPerPage}
										defaultSearchQuery={defaultSearchQuery}
										defaultSortBy={defaultSortBy}
										defaultPageIndex={defaultPageIndex}
										loading={this.state.loading}
										pageCount={this.state.pageCount}
										totalRecordsCount={this.state.totalRecordsCount}
										addRecordComponent={
											can(permissionsMap.adminPanel.items.userManagement.create) &&
											(
												<div className={classes.right}>
													<Button
														color="transparent"
														simple
														aria-label="Invite"
														justIcon
														onClick={this.handleInvite}
													>
														<PersonAddOutlinedIcon />
													</Button>
												</div>
											)
										}
									/>
								</GridItem>
							</GridContainer>
						</CardBody>
					</Card>
				</GridItem>
			</GridContainer>
		);
	}
}

const mapStateToProps = ({
	common: {
		tablesState: {
			users: {
				searchQuery,
				sortBy,
				page,
			},
		},
	},
	settings: { rowsPerPage },
	auth,
}) => {
	return {
		rowsPerPage: rowsPerPage.users,
		defaultSearchQuery: searchQuery,
		defaultSortBy: sortBy,
		defaultPageIndex: page,
		userId: auth?.auth?.id,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(
		setAlertActionCreator,
		dispatch
	),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch,
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch,
	),
});

export default compose(
	withSnackbar,
	connect(mapStateToProps, mapDispatchToProps),
	withStyles(styles),
)(Users);

